import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeftIcon, ArrowRightIcon, MaximizeIcon, PauseIcon, PlayIcon, RotateCcwIcon, XIcon } from "lucide-react";
import { MapleMark } from "../components/MapleMark";
import { TOUR_SCENES } from "../data/tourScenes";
import { EASE } from "../utils/motion";
const LOADER_MS = 1900;
const TAIL_MS = 1400;
function easeInOut(t: number) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
export function Tour() {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const loadedAt = useRef<number | null>(null);
  const assistantOpened = useRef(false);
  const elapsedRef = useRef(0);
  const scene = TOUR_SCENES[index];
  const total = TOUR_SCENES.length;
  useEffect(() => {
    document.title = "MCC Website Walkthrough";
  }, []);
  useEffect(() => {
    setElapsed(0);
    elapsedRef.current = 0;
    loadedAt.current = null;
    assistantOpened.current = false;
  }, [index]);
  useEffect(() => {
    if (!playing) return;
    let last = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const dt = now - last;
      last = now;
      elapsedRef.current += dt;
      setElapsed(elapsedRef.current);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, index]);
  useEffect(() => {
    if (elapsed < scene.duration) return;
    if (index < total - 1) setIndex(i => i + 1);else setPlaying(false);
  }, [elapsed, scene.duration, index, total]);
  useEffect(() => {
    if (scene.kind !== "page" || loadedAt.current === null) return;
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    try {
      const sinceLoad = elapsed - loadedAt.current;
      if (scene.openAssistant && !assistantOpened.current && sinceLoad > LOADER_MS + 1500) {
        assistantOpened.current = true;
        win.dispatchEvent(new Event("mcc:open-enquiry"));
      }
      const portion = scene.scroll ?? 1;
      if (portion === 0) return;
      const span = Math.max(1, scene.duration - loadedAt.current - LOADER_MS - TAIL_MS);
      const p = Math.min(1, Math.max(0, (sinceLoad - LOADER_MS) / span));
      const max = win.document.documentElement.scrollHeight - win.innerHeight;
      win.scrollTo({
        top: easeInOut(p) * max * portion,
        behavior: "instant" as ScrollBehavior
      });
    } catch {
      // Cross-origin frames cannot be scrolled; the scene simply stays at the top.
    }
  }, [elapsed, scene]);
  function go(delta: number) {
    setIndex(i => Math.min(total - 1, Math.max(0, i + delta)));
    setPlaying(true);
  }
  function restart() {
    setIndex(0);
    setElapsed(0);
    elapsedRef.current = 0;
    setPlaying(true);
  }
  function fullscreen() {
    if (document.fullscreenElement) document.exitFullscreen();else document.documentElement.requestFullscreen?.();
  }
  const progress = Math.min(1, elapsed / scene.duration);
  const mobile = scene.kind === "page" && scene.device === "mobile";
  return <div className="flex h-screen w-full flex-col overflow-hidden bg-[#040f1c] text-white" data-id="element-1816" data-mp="fmy5awi59gk9">
      <header className="flex h-14 shrink-0 items-center justify-between px-5 md:px-8" data-id="element-1817" data-mp="khc5rdbnydvs">
        <div className="flex items-center gap-3" data-id="element-1818" data-mp="oj75iddy4j7g">
          <MapleMark size={24} className="text-[#f0b1b9]" data-id="element-1819" data-mp="tfeekf1n2atm" />
          <span className="text-[11px] font-bold tracking-[0.2em]" data-id="element-1820" data-mp="ti0k694yyg97">MCC · WEBSITE WALKTHROUGH</span>
        </div>
        <div className="flex items-center gap-4 text-xs text-white/50" data-id="element-1821" data-mp="13edacx1b2bz7">
          <span className="tabular-nums" data-id="element-1822" data-mp="13cap0qb3l70t">{String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>
          <Link to="/" className="grid h-8 w-8 place-items-center border border-white/20 text-white/70 hover:border-white hover:text-white" aria-label="Exit walkthrough" data-id="element-1823" data-mp="355qg1ob3knwb"><XIcon size={14} data-id="element-1824" data-mp="zfwiwqbw78h5" /></Link>
        </div>
      </header>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 md:px-8" data-id="element-1825" data-mp="uiweejuoyds5">
        <AnimatePresence mode="wait" data-id="element-1826" data-mp="18inxgz02ekd0">
          {scene.kind === "card" ? <motion.div key={`card-${index}`} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.5,
          ease: EASE
        }} className="flex h-full w-full flex-col items-center justify-center text-center" data-id="element-1827" data-mp="13j1kmqo5f64q">
              <MapleMark size={64} animated className="text-[#f0b1b9]" data-id="element-1828" data-mp="bswq3z6od2ps" />
              <motion.p initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.5,
            ease: EASE
          }} className="mt-8 text-xs font-semibold tracking-[0.28em] text-[#f0b1b9]" data-id="element-1829" data-mp="pj0aslpwood4">{scene.eyebrow}</motion.p>
              <div className="mt-6 overflow-hidden" data-id="element-1830" data-mp="3t725f3c10iad">
                <motion.h1 initial={{
              y: "105%"
            }} animate={{
              y: "0%"
            }} transition={{
              duration: 0.9,
              delay: 0.7,
              ease: EASE
            }} className="max-w-5xl text-[clamp(2.8rem,7vw,7rem)] font-medium leading-[0.92] tracking-[-0.07em]" data-id="element-1831" data-mp="3o9jux8id1v09">{scene.title}</motion.h1>
              </div>
              <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.8,
            delay: 1.3,
            ease: EASE
          }} className="mt-8 max-w-xl text-lg text-white/65" data-id="element-1832" data-mp="28k2k0drjlvfe">{scene.subtitle}</motion.p>
              <motion.div initial={{
            scaleX: 0
          }} animate={{
            scaleX: 1
          }} transition={{
            duration: 1,
            delay: 1.5,
            ease: EASE
          }} className="mt-10 h-px w-24 origin-center bg-[#c5233d]" data-id="element-1833" data-mp="1zna0bbqfzwc2" />
            </motion.div> : <motion.div key={`page-${index}`} initial={{
          opacity: 0,
          scale: 0.97,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.985
        }} transition={{
          duration: 0.6,
          ease: EASE
        }} className={`relative flex h-full flex-col overflow-hidden border border-white/10 bg-white shadow-[0_40px_120px_rgba(0,0,0,0.5)] ${mobile ? "my-3 w-[390px] max-w-full rounded-[32px] border-[10px] border-[#1a2533]" : "w-full max-w-[1500px]"}`} data-id="element-1834" data-mp="2dhb10b0yftf2">
              {!mobile && <div className="flex h-9 shrink-0 items-center gap-2 bg-[#0d1a2a] px-4" data-id="element-1835" data-mp="izn2mryybgkb">
                  <span className="h-2.5 w-2.5 rounded-full bg-white/20" data-id="element-1836" data-mp="e4g4dzogb42s" />
                  <span className="h-2.5 w-2.5 rounded-full bg-white/20" data-id="element-1837" data-mp="96y1apx47j2p" />
                  <span className="h-2.5 w-2.5 rounded-full bg-white/20" data-id="element-1838" data-mp="49fqetzjets2" />
                  <span className="ml-4 flex-1 truncate rounded-sm bg-white/10 px-3 py-1 text-[11px] text-white/55" data-id="element-1839" data-mp="3vexlvshbrb2u">maplecc.ca{scene.path === "/" ? "" : scene.path}</span>
                </div>}
              <iframe ref={iframeRef} title={scene.title} src={scene.path} onLoad={() => {
            loadedAt.current = elapsedRef.current;
          }} className="w-full flex-1 border-0 bg-[#fbfaf6]" data-id="element-1840" data-mp="3meiymnksvlwp" />
            </motion.div>}
        </AnimatePresence>
      </div>

      <footer className="shrink-0 px-5 pb-5 pt-4 md:px-8" data-id="element-1841" data-mp="1x58hrnid39rg">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between" data-id="element-1842" data-mp="1x3dyseqnhrq0">
          <div className="min-h-[64px] max-w-3xl" data-id="element-1843" data-mp="21wgvuwumzjay">
            <AnimatePresence mode="wait" data-id="element-1844" data-mp="26us3r21dfjqj">
              <motion.div key={index} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.4,
              ease: EASE
            }} data-id="element-1845" data-mp="26tqk496ahox7">
                <p className="text-xl font-medium tracking-[-0.03em] md:text-2xl" data-id="element-1846" data-mp="1e5kx1frrjnu7">{scene.kind === "page" ? scene.title : scene.eyebrow}</p>
                {scene.kind === "page" && <p className="mt-1 text-sm leading-6 text-white/60" data-id="element-1847" data-mp="1n72k5i3l8pse">{scene.caption}</p>}
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="flex shrink-0 items-center gap-2" data-id="element-1848" data-mp="2uk2xhzkb0dv6">
            <button type="button" onClick={() => go(-1)} className="grid h-10 w-10 place-items-center border border-white/25 hover:border-white" aria-label="Previous scene" data-id="element-1849" data-mp="2yloh03rcfqec"><ArrowLeftIcon size={16} data-id="element-1850" data-mp="3klnfqggxokif" /></button>
            <button type="button" onClick={() => setPlaying(p => !p)} className="grid h-10 w-10 place-items-center bg-[#c5233d] hover:bg-[#a71931]" aria-label={playing ? "Pause walkthrough" : "Play walkthrough"} data-id="element-1851" data-mp="3fpg6adpbwqvq">{playing ? <PauseIcon size={16} data-id="element-1852" data-mp="3plt85yv12b1k" /> : <PlayIcon size={16} data-id="element-1853" data-mp="3plji6b9zg3vv" />}</button>
            <button type="button" onClick={() => go(1)} className="grid h-10 w-10 place-items-center border border-white/25 hover:border-white" aria-label="Next scene" data-id="element-1854" data-mp="78lebta2kqwf"><ArrowRightIcon size={16} data-id="element-1855" data-mp="29ijf1nt0126" /></button>
            <button type="button" onClick={restart} className="grid h-10 w-10 place-items-center border border-white/25 hover:border-white" aria-label="Restart walkthrough" data-id="element-1856" data-mp="h7koxheeawm2"><RotateCcwIcon size={15} data-id="element-1857" data-mp="c5v9lpk53ws6" /></button>
            <button type="button" onClick={fullscreen} className="grid h-10 w-10 place-items-center border border-white/25 hover:border-white" aria-label="Toggle full screen" data-id="element-1858" data-mp="2ixmd761tk29l"><MaximizeIcon size={15} data-id="element-1859" data-mp="2e1nunz369eh3" /></button>
          </div>
        </div>
        <div className="mt-4 flex gap-1" data-id="element-1860" data-mp="ti27c5kbkenr">
          {TOUR_SCENES.map((s, i) => <button key={i} type="button" onClick={() => {
          setIndex(i);
          setPlaying(true);
        }} className="h-3 flex-1" aria-label={`Go to scene ${i + 1}`} data-id="element-1861" data-mp="yf24ts33s939" data-mpi={i}>
              <span className="block h-[2px] w-full overflow-hidden bg-white/15" data-id="element-1862" data-mp="oqaefodoxwug">
                <span className="block h-full origin-left bg-white" style={{
              transform: `scaleX(${i < index ? 1 : i === index ? progress : 0})`
            }} data-id="element-1863" data-mp="oo7fufyh2ve4" />
              </span>
            </button>)}
        </div>
      </footer>
    </div>;
}